import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { AP_TAB, apListPath } from "@/accounts-payable/utils";
import { businessesKey, businessKey } from "@/business/query";
import { quickbooksKey } from "@/business/quickbooks-query";
import { useBusiness } from "@/core/business";

const QUICKBOOKS_CALLBACK_ERRORS: Record<string, string> = {
  invalid_state: "Connection link expired, try again.",
  business_not_found: "Business not found.",
  exchange_failed: "QuickBooks rejected the connection. Try again.",
  missing_params: "QuickBooks returned an incomplete response. Try again.",
};

export const QuickbooksCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessId } = useBusiness();

  useEffect(() => {
    const status = searchParams.get("status");
    const callbackBusinessId = searchParams.get("business_id");
    const targetBusinessId = callbackBusinessId ?? businessId;

    const completeCallback = async () => {
      if (status === "connected") {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: businessesKey }),
          targetBusinessId &&
            queryClient.invalidateQueries({
              queryKey: businessKey(targetBusinessId),
            }),
          targetBusinessId &&
            queryClient.invalidateQueries({
              queryKey: quickbooksKey(targetBusinessId),
            }),
        ]);

        toast.success("QuickBooks connected");

        // The QuickBooks view shows the bill settings to fill in next.
        void navigate(apListPath(AP_TAB.quickbooks), { replace: true });
        return;
      }

      toast.error(
        status
          ? (QUICKBOOKS_CALLBACK_ERRORS[status] ??
              "QuickBooks connection failed. Try again.")
          : "QuickBooks connection failed. Try again.",
      );
      // Back where Connect was pressed, to try again.
      void navigate(apListPath(AP_TAB.quickbooks), { replace: true });
    };

    void completeCallback();
  }, [businessId, navigate, queryClient, searchParams]);

  return (
    <Stack
      gap={4}
      align="center"
      justify="center"
      className="h-full w-full p-4"
    >
      <Stack gap={4} align="center">
        <Spinner
          layout="centered"
          size="lg"
          aria-label="Completing your QuickBooks connection"
        />
        <Text variant="title" className="text-center">
          Completing your QuickBooks connection…
        </Text>
        <Text className="text-center text-subtle">
          We’re syncing your connection and sending you back now.
        </Text>
      </Stack>
    </Stack>
  );
};
